<template>
    <div class="share-page">
        <!-- Loading State -->
        <div v-if="isLoading" class="state-card">
            <div class="spinner"></div>
            <p class="state-text">載入規則中…</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="state-card error-card">
            <div class="state-icon">⚠️</div>
            <h2>找不到規則</h2>
            <p class="state-text">此連結可能已失效或不存在。</p>
        </div>

        <!-- Success / Preview State -->
        <div v-else-if="profile" class="preview-card">
            <div class="share-badge">🔗 分享規則</div>
            <h1 class="profile-name">{{ profile.name }}</h1>

            <div class="meta-row" v-if="profile.created_at">
                <span class="meta-icon">🕐</span>
                <span class="meta-text">最後更新：{{ formatDate(profile.created_at) }}</span>
            </div>

            <div class="stats-row">
                <div class="stat-item">
                    <span class="stat-number">{{ totalOptions }}</span>
                    <span class="stat-label">個規則項目</span>
                </div>
                <div class="stat-item">
                    <span class="stat-number">{{ profile.groups.length }}</span>
                    <span class="stat-label">個分類</span>
                </div>
            </div>

            <div class="groups-preview">
                <div v-for="group in profile.groups" :key="group.name" class="group-chip">
                    <span class="group-chip-name">{{ group.name }}</span>
                    <span class="group-chip-count">{{ group.options.length }}</span>
                </div>
            </div>

            <div v-if="saveSuccess" class="success-banner">
                ✅ 規則已儲存！
            </div>

            <div v-else-if="isSignedIn" class="action-area">
                <button
                    class="action-btn primary"
                    @click="handleSave"
                    :disabled="isSaving"
                >
                    <span v-if="isSaving" class="btn-spinner"></span>
                    {{ isSaving ? '儲存中…' : '💾 儲存此規則' }}
                </button>
            </div>

            <div v-else class="login-prompt">
                <p>📲 在手機 App 開啟此連結以登入並儲存規則。</p>
                <button class="action-btn primary" @click="openInApp">
                    📱 在 App 中開啟
                </button>
                <p class="hint-text">沒有安裝 App？<a :href="webOrigin + '/'" target="_blank">在此使用網頁版</a></p>
            </div>
        </div>
    </div>
</template>

<script>
    import { fetchSharedProfile } from '../services/SupabaseService'
    import { state, importProfileFromShared } from '../assets/scores.js'
    import { APP_WEB_ORIGIN } from '../config.js'

    export default {
        data() {
            return {
                isLoading: true,
                error: null,
                profile: null,
                isSaving: false,
                saveSuccess: false,
                state,
                webOrigin: APP_WEB_ORIGIN,
            }
        },
        computed: {
            isSignedIn() {
                return state.isSignedIn
            },
            totalOptions() {
                if (!this.profile) return 0
                return this.profile.groups.reduce((sum, g) => sum + g.options.length, 0)
            }
        },
        async mounted() {
            const token = this.$route.params.token
            try {
                this.profile = await fetchSharedProfile(token)
            } catch (e) {
                console.error('Failed to fetch shared profile:', e)
                this.error = e.message
            } finally {
                this.isLoading = false
            }
        },
        methods: {
            async handleSave() {
                if (!this.profile) return
                this.isSaving = true
                try {
                    const success = await importProfileFromShared(this.profile.name, this.profile.groups)
                    if (success) {
                        this.saveSuccess = true
                        setTimeout(() => {
                            this.$router.push('/settings')
                        }, 1500)
                    } else {
                        alert('儲存失敗，請先登入。')
                        this.$router.push('/settings')
                    }
                } catch (e) {
                    console.error('Save failed:', e)
                    alert('儲存失敗，請再試一次。')
                } finally {
                    this.isSaving = false
                }
            },
            openInApp() {
                // Deep link directly into the app
                const token = this.$route.params.token
                const appUrl = `${APP_WEB_ORIGIN}/share/${token}`

                // Try to open via Universal Link / App Links — just navigating to the
                // production URL will trigger the OS to redirect to the app if installed.
                // We open this in the same window so the OS can intercept it.
                window.location.href = appUrl
            },
            formatDate(isoString) {
                if (!isoString) return ''
                const date = new Date(isoString)
                const y = date.getFullYear()
                const m = String(date.getMonth() + 1).padStart(2, '0')
                const d = String(date.getDate()).padStart(2, '0')
                const h = String(date.getHours()).padStart(2, '0')
                const min = String(date.getMinutes()).padStart(2, '0')
                return `${y}-${m}-${d} ${h}:${min}`
            }
        }
    }
</script>

<style scoped>
    .share-page {
        min-height: 100vh;
        background: linear-gradient(135deg, #f0fdf4 0%, #e8f5e9 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    }

    /* Loading / Error State Card */
    .state-card {
        background: #fff;
        border-radius: 20px;
        padding: 48px 32px;
        text-align: center;
        box-shadow: 0 8px 30px rgba(0,0,0,0.08);
        max-width: 360px;
        width: 100%;
    }

    .error-card {
        border-top: 4px solid #ef5350;
    }

    .state-icon {
        font-size: 48px;
        margin-bottom: 16px;
    }

    .state-card h2 {
        font-size: 20px;
        color: #333;
        margin: 0 0 8px;
    }

    .state-text {
        color: #888;
        font-size: 14px;
        margin: 0 0 24px;
    }

    /* Spinner */
    .spinner {
        width: 40px;
        height: 40px;
        border: 3px solid #e8f5e9;
        border-top-color: #4CAF50;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
        margin: 0 auto 20px;
    }

    @keyframes spin {
        to { transform: rotate(360deg); }
    }

    /* Preview Card */
    .preview-card {
        background: #fff;
        border-radius: 24px;
        padding: 36px 28px;
        text-align: center;
        box-shadow: 0 12px 40px rgba(76, 175, 80, 0.12);
        max-width: 420px;
        width: 100%;
        border-top: 4px solid #4CAF50;
    }

    .share-badge {
        display: inline-block;
        background: #e8f5e9;
        color: #2e7d32;
        font-size: 13px;
        font-weight: 600;
        padding: 4px 14px;
        border-radius: 20px;
        margin-bottom: 16px;
        border: 1px solid #c8e6c9;
    }

    .profile-name {
        font-size: 26px;
        font-weight: 700;
        color: #1b5e20;
        margin: 0 0 12px;
        line-height: 1.2;
    }

    /* Last updated meta row */
    .meta-row {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
        margin-bottom: 20px;
    }

    .meta-icon {
        font-size: 13px;
    }

    .meta-text {
        font-size: 12px;
        color: #aaa;
    }

    /* Stats Row */
    .stats-row {
        display: flex;
        gap: 16px;
        justify-content: center;
        margin-bottom: 24px;
    }

    .stat-item {
        background: #f5f5f5;
        border-radius: 12px;
        padding: 14px 22px;
        min-width: 90px;
    }

    .stat-number {
        display: block;
        font-size: 28px;
        font-weight: 700;
        color: #4CAF50;
        line-height: 1;
    }

    .stat-label {
        display: block;
        font-size: 11px;
        color: #999;
        margin-top: 4px;
    }

    /* Groups Preview */
    .groups-preview {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        justify-content: center;
        margin-bottom: 28px;
    }

    .group-chip {
        display: flex;
        align-items: center;
        gap: 6px;
        background: #f9f9f9;
        border: 1px solid #eee;
        border-radius: 20px;
        padding: 5px 12px;
        font-size: 12px;
    }

    .group-chip-name {
        color: #555;
    }

    .group-chip-count {
        background: #e8f5e9;
        color: #2e7d32;
        border-radius: 10px;
        padding: 1px 7px;
        font-size: 11px;
        font-weight: 600;
    }

    /* Success Banner */
    .success-banner {
        background: #e8f5e9;
        color: #2e7d32;
        border-radius: 12px;
        padding: 16px;
        font-size: 16px;
        font-weight: 600;
        margin-top: 8px;
    }

    /* Action Area */
    .action-area,
    .login-prompt {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .login-prompt p {
        font-size: 13px;
        color: #666;
        margin: 0 0 4px;
        line-height: 1.5;
    }

    .hint-text {
        font-size: 12px !important;
        color: #bbb !important;
        margin-top: 0 !important;
    }

    .hint-text a {
        color: #4CAF50;
        text-decoration: none;
    }

    .hint-text a:hover {
        text-decoration: underline;
    }

    /* Buttons */
    .action-btn {
        width: 100%;
        padding: 14px;
        border-radius: 12px;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        border: none;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
    }

    .action-btn.primary {
        background: #4CAF50;
        color: white;
        box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
    }

    .action-btn.primary:hover:not(:disabled) {
        background: #43a047;
        transform: translateY(-1px);
        box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4);
    }

    .action-btn.primary:active:not(:disabled) {
        transform: scale(0.98);
    }

    .action-btn.primary:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }

    /* Button spinner */
    .btn-spinner {
        width: 16px;
        height: 16px;
        border: 2px solid rgba(255,255,255,0.4);
        border-top-color: white;
        border-radius: 50%;
        animation: spin 0.7s linear infinite;
        flex-shrink: 0;
    }
</style>
